import { type SelectOption } from "@/shared/components/forms/ColocaSelectField";
import { isOnboardingCountryIso3 } from "@/shared/data/onboarding-countries";
import { ALL_PHONE_COUNTRIES } from "@/shared/data/phone-countries";
import type { CountryLabelResolver } from "@/shared/hooks/useCountryLabel";
import { countries } from "@/shared/utils/countries";

import { flagFromIso2 } from "../../constants/countries.constants";

// Countries the identity selectors offer: the onboarding allowlist, the same 17 the public register offers.
//
// This used to be the six countries with a specific document matrix, on the stated grounds that "the backend
// `@IsCountryCode` only accepts the six supported ISO 3166-1 alpha-3 codes". **That was false**: the enum
// behind that decorator has 189 entries, and `documentTypesFor` already falls back to the generic document
// list for a country outside the matrix — the fallback exists for exactly this. The six-country cap produced
// a live defect instead: somebody resident in Costa Rica, Guatemala, Panama, Paraguay, Uruguay, Ecuador,
// Germany, Spain, France, Italy or Portugal could register and then never complete verification, because
// their country was not on offer here.
//
// Narrower than the register would reopen that gap; wider would offer a country onboarding refuses.
const OFFERED_COUNTRIES = countries.filter((c) => !!c.iso2 && isOnboardingCountryIso3(c.code));

/**
 * Country options for the identity selectors, named in the active locale and sorted by that name.
 *
 * Takes the resolver rather than calling `useCountryLabel` itself so it stays a plain function: the two
 * holder forms build their options inside a `useMemo` keyed on it.
 */
export const countryItems = (labelFor: CountryLabelResolver, locale: string): SelectOption[] => {
  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  return OFFERED_COUNTRIES.map((c) => ({
    iso2: c.iso2 as string,
    iso3: c.code,
    name: labelFor(c.code, c.name),
  }))
    .sort((a, b) => collator.compare(a.name, b.name))
    .map((c) => ({
      value: c.iso3,
      label: (
        <span className="inline-flex items-center gap-2">
          <span aria-hidden="true">{flagFromIso2(c.iso2)}</span>
          <span>{c.name}</span>
        </span>
      ),
    }));
};

/**
 * Resolves a stored country (alpha-2 or alpha-3) to the alpha-3 code the selectors offer, or `undefined`.
 *
 * Used to seed a form from the signed-in user's organization: a country we do not offer must not be
 * preselected, so the caller falls back to its own default.
 */
export const offeredIdentityCountry = (code: string | null | undefined): string | undefined => {
  const upper = code?.trim().toUpperCase();
  if (!upper) return undefined;
  const iso3 = countries.find((c) => c.iso2 === upper || c.code === upper)?.code;
  return iso3 && isOnboardingCountryIso3(iso3) ? iso3 : undefined;
};

/**
 * The first of the given countries we actually onboard from, in the order given, or `undefined`.
 *
 * `offeredIdentityCountry(a ?? b)` is the trap this exists to close: `??` resolves over the RAW codes, so it
 * stops at the first one that is merely PRESENT. Somebody whose own country is one we do not offer — a US
 * resident, which the split between the phone and residence lists made a reachable state — never reached
 * their organization's country, even when that one was on offer, and both selectors opened on the constant.
 *
 * The two holder forms pass the same two codes in opposite orders, which is the whole of the difference
 * between them; stating the rule here is what keeps it from being re-derived twice.
 */
export const firstOfferedIdentityCountry = (
  ...codes: readonly (string | null | undefined)[]
): string | undefined => {
  for (const code of codes) {
    const iso3 = offeredIdentityCountry(code);
    if (iso3) return iso3;
  }
  return undefined;
};

/**
 * Phone-field seed for an identity country: its alpha-2 code and dial code.
 *
 * These forms run authenticated, so the signed-in user's country is a far better guess than a constant — they
 * chose it when they registered. The public register has to hard-code the home market because nobody has
 * identified themselves yet; here that would be throwing away what we already know.
 *
 * Returns both halves because the two travel to different places: the alpha-2 seeds `phone_country` and the
 * picker's `defaultCountry`, the dial code seeds the input's value. Resolving them separately in each form is
 * how they drift apart.
 */
export const phoneSeedFor = (iso3: string): { iso2: string; dialCode: string } => {
  const iso2 = countries.find((c) => c.code === iso3.toUpperCase())?.iso2 ?? "";
  const dialCode = ALL_PHONE_COUNTRIES.find((c) => c.iso2 === iso2)?.dialCode ?? "";
  return { iso2, dialCode };
};

// Backend's IsInternationalPhone validator rejects non-digits.
export const normalizePhone = (raw: string): string => raw.replace(/\D/g, "");
